import { postContentClass } from "@repo/fika-blog/src/classes"
import {
  postClass,
  postContainerClass,
  postDateClass,
  postImageClass,
} from "@repo/fika-blog/src/content.css"
import { extensions } from "@repo/fika-blog/src/extensions"
import { initImageZoom } from "@repo/fika-blog/src/image-zoom"
import { escapeHTML } from "@repo/fika-blog/src/lib/escape-html"
import { Subscribe } from "@repo/fika-blog/src/nodes/subscribe"
import {
  authorClass,
  postDescriptionClass,
  postHeaderAuthorClass,
  postHeaderClass,
  postHeaderContentClass,
  postHeaderDateWrapClass,
  postImageWrapperClass,
  postTitleClass,
} from "@repo/fika-blog/src/post-header.css"
import { cdnClass } from "@repo/fika-design-system/cdn-class"
import { renderToHTMLString } from "@tiptap/static-renderer/pm/html-string"
import { format } from "date-fns"
import { createMemo, type JSX, type ParentProps, Show } from "solid-js"
import { getTitle } from "@/lib/get-title"
import { ResponsiveImage } from "../components/responsive-image"
import {
  postHeaderAuthorImageClass,
  postPreviewClass,
  truncatedContentClass,
} from "./post-preview.css"

interface PostPreviewData {
  title: string | null
  description: string | null
  imageUrl: string | null
  date: string | Date
  content?: string | null
  authorName?: string | null
  authorProfileImage?: string | null
  authorProfileUrl?: string | null
}

type PostPreviewProps = {
  post: PostPreviewData
  loading?: boolean
  subscribed?: boolean
  subscribedLabel?: string
  subscribedTitle?: string
  truncated?: boolean
  headerExtra?: JSX.Element
  afterHeader?: JSX.Element
  contentAreaClass?: string
  contentAreaExtra?: JSX.Element
  contentRef?: (el: HTMLDivElement) => void
  defaultSubscribeFormId?: string | null
} & ParentProps

export function PostPreview(props: PostPreviewProps) {
  initImageZoom()

  const html = createMemo(() => {
    const content = props.post.content
    if (!content) return null
    try {
      const subscribed =
        props.subscribed && props.subscribedLabel && props.subscribedTitle

      return renderToHTMLString({
        extensions: extensions({
          dropcursor: false,
          Subscribe: subscribed
            ? Subscribe.configure({
                subscribed: true,
                subscribedLabel: props.subscribedLabel!,
                subscribedTitle: props.subscribedTitle!,
              })
            : Subscribe.configure({
                defaultFormId: props.defaultSubscribeFormId ?? null,
              }),
        }),
        content: JSON.parse(content),
        options: {
          nodeMapping: {
            text: ({ node }: { node: { text?: string } }) =>
              escapeHTML(node.text ?? ""),
          },
        },
      })
    } catch {
      return null
    }
  })

  const formatDate = createMemo(() => format(new Date(props.post.date), "PP"))

  return (
    <div
      class={`${postContainerClass} ${postPreviewClass({ loading: props.loading ?? false })}`}
    >
      <article class={postClass}>
        <header class={postHeaderClass}>
          <div class={postHeaderContentClass}>
            <h1 class={postTitleClass}>{getTitle(props.post.title)}</h1>
            <Show when={props.post.description}>
              {description => (
                <h2 class={postDescriptionClass}>{description()}</h2>
              )}
            </Show>
          </div>
          <Show when={props.post.imageUrl}>
            {imageUrl => (
              <figure class={postImageWrapperClass}>
                <img
                  src={imageUrl()}
                  alt={props.post.title ?? ""}
                  class={postImageClass}
                  data-zoom
                  srcset={`
                    ${cdnClass(imageUrl(), "post300")} 300w,
                    ${cdnClass(imageUrl(), "post400")} 400w,
                    ${cdnClass(imageUrl(), "post600")} 600w,
                    ${cdnClass(imageUrl(), "post1000")} 1000w
                  `}
                  sizes="
                    (max-width: 400px) 300px,
                    (max-width: 500px) 400px,
                    (max-width: 600px) 600px,
                    1000px
                  "
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            )}
          </Show>
          <div class={postHeaderAuthorClass}>
            <Show when={props.post.authorProfileImage}>
              {profileImage => (
                <ResponsiveImage
                  image={{
                    url: profileImage(),
                    imageClass: "logo",
                  }}
                  class={postHeaderAuthorImageClass}
                  alt={props.post.authorName ?? ""}
                />
              )}
            </Show>
            <Show when={props.post.authorName}>
              {authorName => (
                <Show
                  when={props.post.authorProfileUrl}
                  fallback={<span class={authorClass}>{authorName()}</span>}
                >
                  {url => (
                    <a
                      href={url()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class={authorClass}
                    >
                      {authorName()}
                    </a>
                  )}
                </Show>
              )}
            </Show>
            <div class={postHeaderDateWrapClass}>
              {props.headerExtra}
              <p class={postDateClass}>{formatDate()}</p>
            </div>
          </div>
        </header>
        {props.afterHeader}
        <Show
          when={props.contentAreaClass}
          fallback={
            <>
              <Show when={html()}>
                {htmlContent => (
                  <div
                    class={
                      props.truncated
                        ? `${postContentClass} ${truncatedContentClass}`
                        : postContentClass
                    }
                    innerHTML={htmlContent()}
                  />
                )}
              </Show>
              {props.children}
            </>
          }
        >
          <div class={props.contentAreaClass} ref={props.contentRef}>
            {props.contentAreaExtra}
            <Show when={html()}>
              {htmlContent => (
                <div
                  class={
                    props.truncated
                      ? `${postContentClass} ${truncatedContentClass}`
                      : postContentClass
                  }
                  innerHTML={htmlContent()}
                />
              )}
            </Show>
            {props.children}
          </div>
        </Show>
      </article>
    </div>
  )
}
